import { ReactNode } from "react";
import { IBookingQuestions } from "../features/EUD/modules/BookingQuestions/IBookingQuestions";
import { ICreator } from "./creator";
import { ITemplate } from "./template";

export namespace IHost {
  export interface IGalleryItem {
    id: number;
    creator: number;
    type: "image" | "video" | "photo";
    media: string;
    caption: string;
    uuid: string;
    priority: number;
  }

  export interface IEventData {
    id: number;
    plan_uuid?: string;
    title: string;
    type: number;
    short_description: string;
    cover_image: string;
    description: string;
    price_per_head: number;
    min_price: number;
    min_price_usd: number;
    max_spots: number;
    priority: number;
    is_draft: boolean;
    is_active: boolean;
    is_hidden: boolean;
    status: number;
    start_date: number;
    total_inventory: number;
    purchased_inventory;
    uuid: string;
    updated_price: number;
    end_date: number;
    interval_type: number;
    currency: string;
    next_slot_time: string;
    upcoming_slot_datetime?: string;
    metadata: {
      keywords?: string[]; // Keywords assigned to a specific listing. This will be a string, and not the IKeyword interface
      [k: string]: any;
    };
    sku_title: string; // title of the category
    is_payment_disabled?: boolean; // Whether to show Express Interest on host page
    org_username?: string | null; // uuid of the member who is assigned this listing
    availability_choice?: number | null; // The creator can set his availability for sessions using this flag
    url_slug?: string;
  }

  export interface ITestimonialItem {
    title?: string;
    id: number;
    creator: number;
    name: string;
    content: string;
    cover_image: string;
    uuid: string;
    type: number;
    media: string;
    priority: number;
  }

  export declare type ITemplateKey =
    | "classic"
    | "elementary"
    | "modern"
    | "unfold"
    | "linktemp"
    | "yoga"
    | "growth"
    | "eduverse"
    | "unlock";

  export interface IFooterSectionProps {
    tnCLink: string;
    customFooterContent?: string;
    overrideFooterDefaultText?: {
      // To override the text shown on either side of the footer
      top_left?: string;
      bottom_left?: string;
      top_right?: string;
      middle_right?: string;
      bottom_right?: string;
    };
    template: ITemplateKey;
    handleClick: (e: React.MouseEvent) => void;
  }

  export interface IPlatformRating {
    icon: string;
    link: string;
    title: string;
    subtitle: string;
  }

  export const BILLING_DISPLAY_FORMAT = {
    DEFAULT_SHOW_ALL: 1,
    SEPARATE_CGST: 2,
    PRETEND_CGST_INAPPLICABLE: 3,
  };

  export type IBillingDisplayFormat =
    (typeof BILLING_DISPLAY_FORMAT)[keyof typeof BILLING_DISPLAY_FORMAT];

  export interface ICreatorTopFeature {
    cta_text?: ReactNode;
    title: string;
    description: string;
    media: string;
    id: number;
    priority: number;
  }

  export interface ICreatorStat {
    value: string;
    description: string;
    id: number;
    priority: number;
    media?: string;
  }

  export interface ICustomCode {
    uuid: string;
    code_snippet: string;
    code_position: Array<number>;
    name: string;
  }

  export type ICustomCodes = Array<ICustomCode>;

  export interface IHostData {
    android_app_url?: string;
    ios_app_url?: string;
    // recorded content
    enable_otp_verification: boolean;
    custom_word_for_course?: string; // custom text in lowercase to replace "course" word for recorded content used across host site
    custom_word_for_course_mark?: string; // custom text in lowercase to replace "mark" word for score in recorded content used across host site
    custom_word_for_course_section?: string; // custom text in lowercase to replace "section" word in recorded content used across host site
    custom_word_for_course_lesson?: string; // custom text in lowercase to replace "lesson" word in recorded content used across host site
    custom_word_for_course_question?: string; // custom text in lowercase to replace "question" word in recorded content used across host site
    custom_word_for_course_exam?: string; // custom text to replace word "exam" everywhere in exam type recorded content

    allow_payment_method_toggle: boolean; // in case of international user if it's true then payment method would be Paypal else Stripe
    about_title?: string; // title used for about section in navbar
    custom_team_navbar_title?: string; // title used for team section
    custom_team_section_title?: string; // title used for learn from best section
    offerings_title?: string; // title used for offerings section in navbar
    cover_pic?: string;
    cover_video?: string;
    country_code: string;
    contact_mode: "Telegram" | "Whatsapp" | "none" | "only_booked_user"; // None, whatsapp, or telegram.
    contact_url: string; // The url to be opened when the contact widget is clicked
    custom_name_header: string;
    custom_listings_header: string;
    categorise_listing?: boolean;
    description?: string;
    recent_sales_popup: boolean; // enable or disable recent sales popup in event page
    exit_intent: boolean; // enable or disable exit intent form
    exit_intent_attributes?: {
      exit_intent_title?: string; // exit intent form title
      exit_intent_description_line_1_start?: string; // exit intent form description line 1 start
      exit_intent_description_line_1_highlight?: string; // exit intent form description line 1 highlight
      exit_intent_description_line_1_end?: string; // exit intent form description line 1 end
      exit_intent_description_line_2?: string; // exit intent form description line 2
      exit_intent_show_email_field?: boolean; // show email field in exit intent form
      exit_intent_show_name_field?: boolean; // show name field in exit intent form
      exit_intent_show_phone_field?: boolean; // show phone number field in exit intent form
      exit_intent_cta?: string; // cta for exit intent form
    };
    feedback: Array<string>; // TODO: What is this
    gallery: {
      title: string;
      data: Array<IGalleryItem>;
      description?: string | undefined;
    };
    seo_preview: {
      title: string;
      description: string;
    };

    visit_website_cta?: string;
    custom_certificate_title?: string;
    global_booking_cta: string;
    intro_video: string;
    is_international_enabled: boolean;
    access_content_cta_text?: string; // title used for cta to content page
    content_start_cta_text?: string; // cta text for viewing an event
    all_offerings_cta_text?: string; // cta for a button on event page to view all offerings
    show_more_offerings_cta_text?: string; // cta to view other offerings in case the chosen one is sold out
    contact_creator_cta_text?: string; // cta for a button on event page to contact creator
    hide_share_event_cta?: boolean; //  hide cta to share a specific event
    hide_venue_details?: boolean; // hide online venue details
    share_event_cta_text?: string; // cta to share a specific event
    meet_creator?: string; // heading used in about section along with creator name
    universal_pay_now_cta_text?: string; // cta on payment page
    interest_cta_text: string;
    internet_handling_percent: string;

    disable_booking_email_whitelisting?: boolean; // if this is true, then we do not have to check the
    // validation - 'getEmailHasValidTopLevelDomain' on every email field

    social_info: {
      logo_text?: string;
      logo_image?: string;
      favicon?: string;

      instagram?: string;
      whatsapp?: string;
      spotify?: string;
      facebook_name?: string;
      twitter?: string;
      linkedin?: string;
      youtube?: string;
      follower_count?: Record<symbol | string, number>;

      experience_years_count?: string;
      experience_years_label?: string;

      interest_helper_text?: string; // Header for get in touch section, needed in some templates
      features_image?: string; // used for top features section in host page
      features_description?: string; // used for top features section in host page
      cover_pic_type?: "color" | "image"; // used for bg type in unfold template

      hero_section_title?: string; // Title of her section
      about_section_title?: string; // About Section title
      mobile_cover_image?: string; // Mobile cover image

      // Put all metadata info here
      [k: string]: any;
    };

    custom_truncate_expand_label?: string; // Custom label for "View more listings" present in some templates
    expand_host_desc?: boolean; // Disable the trimming of host description

    hide_creator_header_event_page?: boolean;
    hide_login_navbar?: boolean; // Whether to hide the login links in navbar (for creators who do not support content like that)
    hide_trustmarker_icons: boolean; // Hides trust marker icons on event page.
    trust_markers?: Array<string>;
    listings: Array<IEventData>;
    categorise_type: number; // categorise listing by tags or subcategories
    sku_title_map: Array<string>; // Denotes the sku title for each sku type. Depreciated, use sku_map instead
    sku_map: Record<number, string>; // Key-value pair indicating the mapping of sku type to sku title
    sku_order: Array<number>; // Denotes the priority order for sku type
    all_keywords: Array<ITemplate.IKeyword>;
    name: string;
    profile_pic: string;
    phone_number: string;
    paypal_link: string;
    short_description: string;
    title: string;
    testimonials: {
      title: string;
      data: Array<ITestimonialItem>;
      visible?: boolean;
    };
    username: string;
    whatsapp_url: string;
    is_premium?: boolean;
    is_zoom_isv_enabled?: boolean;
    custom_t_c?: string;
    custom_t_c_label?: string;
    custom_footer_content?: string; // To insert custom text in the middle of a footer
    override_footer_default_text?: IFooterSectionProps["overrideFooterDefaultText"];
    dropdown_choices: Array<{ title: string; href: string }>;
    rating?: number;
    platform_ratings?: Array<IPlatformRating>;
    cancellation_policy?: string;
    custom_pixel_token?: string;
    custom_fb_access_token?: string;
    custom_fb_test_event_code?: string;
    custom_purchase_pixel_token?: string; // custom pixel token we want to use only for purchase event on succes page
    custom_ga_token?: string;
    custom_meta_script?: string;
    custom_script_tags?: string[];
    user_details?: {
      // Customization for the user details form
      title?: string;
    };
    // Redirect after filling the get in touch form
    post_interest_url?: string;
    hide_get_in_touch_section?: boolean; // hide get in touch section in classic template
    // Added for Self Serve - Webpage Creation
    leads_q_and_a: Array<IBookingQuestions.IQuestion>;
    prepend_plans: boolean; // to show plans first or last when shown on host page
    get_gst_info: boolean; // to ask for gst details while booking
    billing_state_non_mandatory: boolean; // to make billing state non mandatory if user does not fill gst details

    is_international_creator?: boolean; // If the creator's locale is outside India

    // get in touch form attributes
    custom_get_in_touch_form_title?: string;
    custom_get_in_touch_form_field_name?: string;
    custom_get_in_touch_form_field_email?: string;
    custom_get_in_touch_form_field_phone?: string;
    custom_get_in_touch_form_field_message?: string;
    custom_get_in_touch_form_cta?: string;

    gst_summary_type?: IBillingDisplayFormat;

    is_cart_enabled?: boolean;

    categorise_by_keyword?: boolean; // [Misleading name] this dictates if for keyword/no categorisation, the listing sku type will be visible as a label

    // used for TopFeaturesSection in host page
    features: {
      title: string;
      data: Array<ICreatorTopFeature>;
      visible?: boolean;
    };

    // used for StatsSection in host page
    statistics: {
      title: string;
      data: Array<ICreatorStat>;
      visible?: boolean;
    };
    stripe_connect_id?: string;
    custom_bottom_button_text?: string;
    custom_bottom_button_cta?: string;
    custom_codes?: ICustomCodes;

    event_payment_summary_expand_cta_text?: string; // event payment summary expand cta text for event page
    event_payment_summary_tax_label?: string; // custom label for "tax" in expanded payment details
    my_classes_page_title?: string; // custom page title for my classes page

    is_email_non_mandatory_for_booking?: boolean;
    is_page_filter_enabled?: boolean;
    listing_express_interest_cta?: string;

    custom_headings: {
      features_title?: string;
      gallery_description?: string;
      gallery_title?: string;
      testimonials_title?: string;
    };

    show_creator_personal_address?: boolean;
    call_section_title?: string;
    call_section_description?: string;

    custom_order_bump_heading?: string; // section name of order bumps
    custom_order_bump_add_to_cart_cta?: string; // if true redirect to offering page onClick of card
    hide_price_for_zero_priced_order_bump_listing?: boolean;
    personal_address_title?: string;
    listing_express_interest_form_title?: string; // this will override express interest form title
    express_interest_message_behaviour?: number; // enum number to control the behavior of express interest message form

    // custom payment gateway config
    creator_gateway_details?: Array<{
      gateway: string;
      config?: { published_key?: string };
    }>;

    hide_lms_nps_survey: boolean; // hide NPS survey form for this creator
    hide_exam_result_chips?: boolean; // when true, hide PASSED/FAILED chips on exam result and show dynamic score instead
    exam_success_message?: string; // custom message shown on exam pass, overrides all score-range specific messages

    hide_user_details_in_booking_details?: boolean; // hide user details in booking details. used on thank you page
    is_customer_details_updation_allowed?: boolean; // if customer can update their account details while booking with existing account details

    payout_gateway?: number; // payout gateway type PAYOUT_GATEWAY (repoV2/constants/payment.ts)

    einvoice_enabled?: ICreator.ICreatorDetails["einvoice_enabled"]; // refer ICreator.ICreatorDetails["einvoice_enabled"]
  }
}
